import React, { useEffect, useState } from 'react';
import {Lesson, PdfObject, Task, TestTask } from '../constants/AllTypes';

interface LessonContextData {
  currentLesson: Lesson | null;
  setCurrentLesson: (lesson: Lesson | null) => void;
  currentTaskIndex: number;
  setCurrentTaskIndex: (index: number) => void;
  currentTaskId: number | null;
  setCurrentTaskId: (value: number | null) => void;
  selectedTask: Task | null;
  setSelectedTask: (task: Task | null) => void;
  testTasks: TestTask[];
  setTestTasks: React.Dispatch<React.SetStateAction<TestTask[]>>;
  courseId: string | null;
  setCourseId: (value: string | null) => void;
  unitId: string | null;
  setUnitId: (value: string | null) => void;
  task: Task | null;
  setTask: (task: Task | null) => void;
  highlightedTask: number | null; 
  setHighlightedTask: (index: number | null) => void; 
  pdf: PdfObject;
  setPdf: React.Dispatch<React.SetStateAction<PdfObject>>;
  deletedPdfs: PdfObject[]; 
  setDeletedPdfs: React.Dispatch<React.SetStateAction<PdfObject[]>>;
  checkingResult: boolean;
  setCheckingResult: React.Dispatch<React.SetStateAction<boolean>>;
  studentId: number | null;
  setStudentId: React.Dispatch<React.SetStateAction<number | null>>;
  isLessonMode: boolean;
  setIsLessonMode: React.Dispatch<React.SetStateAction<boolean>>;
  homeworkSubmitted: boolean;
  setHomeworkSubmitted: React.Dispatch<React.SetStateAction<boolean>>;
  currentTestAttempt: number;
  setCurrentTestAttempt: React.Dispatch<React.SetStateAction<number>>;
  playAsType: string | null;
  setPlayAsType: React.Dispatch<React.SetStateAction<string | null>>;
}

// Provide a default value for LessonContext
export const LessonContext = React.createContext<LessonContextData>({
  currentLesson: null,
  setCurrentLesson: () => { },
  currentTaskIndex: 0,
  setCurrentTaskIndex: () => { },
  currentTaskId: null, 
  setCurrentTaskId: () => { }, 
  selectedTask: null, 
  setSelectedTask: () => { }, 
  testTasks: [],
  setTestTasks: () => { },
  courseId: null,
  setCourseId: () => { },
  unitId: null,
  setUnitId: () => { },
  task: null,
  setTask: () => { },
  highlightedTask: null, 
  setHighlightedTask: () => { }, 
  pdf: { pdfFile: null, selectedPages: [], totalPages: 0, isUploaded: false },
  setPdf: () => { },
  deletedPdfs: [],
  setDeletedPdfs: () => { },
  checkingResult: false,
  setCheckingResult: () => { },
  studentId: null,
  setStudentId: () => { },
  isLessonMode: false,
  setIsLessonMode: () => { },
  homeworkSubmitted: false,
  setHomeworkSubmitted: () => { },
  currentTestAttempt: 0,
  setCurrentTestAttempt: () => { },
  playAsType: null,
  setPlayAsType: () => { },
});

export const LessonProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {

  const [currentLesson, setCurrentLessonState] = useState<Lesson | null>(() => {
    const storedLesson = sessionStorage.getItem('lesson');
    return storedLesson ? JSON.parse(storedLesson) : null;
  });

  const setCurrentLesson = (value: Lesson | null) => {
   
    setCurrentLessonState(value);
    if (value === null) {
      sessionStorage.removeItem('lesson');
    } else {
      sessionStorage.setItem('lesson', JSON.stringify(value));
    }
  };

  const [currentTaskIndex, setCurrentTaskIndexState] = useState<number>(0);
  const [currentTaskId, setCurrentTaskIdState] = useState<number | null>(null);
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  const [courseId, setCourseId] = useState<string | null>(null);
  const [unitId, setUnitId] = useState<string | null>(null);
  const [task, setTask] = useState<Task | null>(null);
  const [highlightedTask, setHighlightedTask] = useState<null | number>(null);
  const [pdf, setPdf] = useState<PdfObject>({ pdfFile: null, selectedPages: [], totalPages: 0, isUploaded: false });
  const [deletedPdfs, setDeletedPdfs] = useState<PdfObject[]>([]);
  const [checkingResult, setCheckingResult] = useState<boolean>(() => {
    // Initialize state from local storage if available
    const savedResult = localStorage.getItem('checkingResult');
    return savedResult ? JSON.parse(savedResult) : false; 
  });
  const [studentId, setStudentId] = useState<number | null>(null);
  const [testTasks, setTestTasks] = useState<TestTask[]>([]);
  const [isLessonMode, setIsLessonMode] = useState(false);
  const [homeworkSubmitted, setHomeworkSubmitted] = useState(false);
  const [currentTestAttempt, setCurrentTestAttempt] = useState<number>(0);
  const [playAsType, setPlayAsType] = useState<string | null>(null);

  // Ephemeral "play as" override — never carries across tasks or out of lesson mode.
  useEffect(() => {
    setPlayAsType(null);
  }, [task?.taskId, isLessonMode]);

  const setCurrentTaskIndex = (index: number) => {
    setCurrentTaskIndexState(index);
  };

  const setCurrentTaskId = (value: number | null) => {
    setCurrentTaskIdState(value);
  };

  useEffect(() => {
    localStorage.setItem('checkingResult', JSON.stringify(checkingResult));
  }, [checkingResult]);

  return (
    <LessonContext.Provider value={{
      currentLesson, setCurrentLesson, currentTaskIndex, setCurrentTaskIndex,
      currentTaskId, setCurrentTaskId, selectedTask, setSelectedTask,
      courseId, setCourseId, unitId, setUnitId, task, setTask, highlightedTask, setHighlightedTask,
      pdf, setPdf, deletedPdfs, setDeletedPdfs, checkingResult, setCheckingResult, testTasks, setTestTasks,
      studentId, setStudentId, isLessonMode, setIsLessonMode, homeworkSubmitted, setHomeworkSubmitted, currentTestAttempt, setCurrentTestAttempt,
      playAsType, setPlayAsType
    }}>
      {children}
    </LessonContext.Provider>
  );
};